import { apiEndpoints } from '@/lib/api/endpoints'
import { appPath } from '@/lib/getPublicAppUrl'

export interface DirectUploadMetadata {
    parent_type?: string
    parent_id?: string
    source_type?: string
    source_id?: string
}

export interface DirectUploadResult {
    id: string
    filename: string
    content_type: string
    created_at: string
    state?: string
    ready?: boolean
}

interface PresignResponse {
    id: string
    filename: string
    content_type: string
    created_at: string
    upload_url: string
    required_headers: Record<string, string>
    expires_at: string
    state?: string
    ready?: boolean
}

function uploadError(message: string, status: number): Error & { status: number } {
    return Object.assign(new Error(message), { status })
}

/** `path` is an app-relative endpoint such as `apiEndpoints.fileUploads.presign`. */
async function sameOriginJson<T>(path: string, init: RequestInit): Promise<T> {
    const response = await fetch(appPath(path), {
        credentials: 'include',
        ...init,
        headers: {
            'Content-Type': 'application/json',
            ...(init.headers ?? {}),
        },
    })

    if (!response.ok) {
        throw uploadError('file_upload_failed', response.status)
    }

    return response.json() as Promise<T>
}

/**
 * Reserves a FileUpload via same-origin presign, PUTs bytes straight to S3, then
 * completes on Core. Avoids Vercel's ~4.5MB proxy limit and cross-origin cookie issues
 * on partner storefronts (host-only session cookies never reach api.*).
 */
export async function uploadFileDirectToS3(
    file: File,
    metadata: DirectUploadMetadata = {},
): Promise<DirectUploadResult> {
    const contentType = file.type || 'application/octet-stream'

    const presign = await sameOriginJson<PresignResponse>(apiEndpoints.fileUploads.presign, {
        method: 'POST',
        body: JSON.stringify({
            filename: file.name,
            content_type: contentType,
            byte_size: file.size,
            ...metadata,
        }),
    })

    const putResponse = await fetch(presign.upload_url, {
        method: 'PUT',
        headers: presign.required_headers,
        body: file,
    })

    if (!putResponse.ok) {
        throw uploadError('file_upload_s3_failed', putResponse.status)
    }

    return sameOriginJson<DirectUploadResult>(apiEndpoints.fileUploads.complete(presign.id), {
        method: 'POST',
        body: JSON.stringify({}),
    })
}
